/**
 * 存储装配点（唯一的替换位置）
 * ------------------------------------------------------------
 * 想换成云端数据库时，只改这个文件：
 *
 *   import { SupabaseAdapter } from './supabase.js';
 *   const adapter = new SupabaseAdapter(env);
 *
 * UI、service、engine 全部通过 getStorage() 拿实例，不感知实现。
 */
import { IndexedDBAdapter } from './indexeddb.js';
import { MemoryAdapter } from './memory.js';
import { assertAdapter } from './adapter.js';

export function createStorage(kind = 'indexeddb') {
  if (kind === 'memory') return assertAdapter(new MemoryAdapter());
  if (kind === 'indexeddb') return assertAdapter(new IndexedDBAdapter());
  throw new Error(`未知存储类型: ${kind}`);
}

/**
 * 解析 URL 里请求的存储类型：?storage=memory 为无持久化演示模式
 * ------------------------------------------------------------
 * 两个地方都可能带着它：
 *   1) location.search          /?storage=memory#/home
 *   2) hash 里的查询串          /#/home?storage=memory
 * 第 2 种是 main.js 的 normalizeEntryHash() 规整后的结果。
 *
 * ⚠️ 这里必须两种都看：ESM 的静态 import 会先于 main.js 的模块体执行，
 * 如果某个模块在**加载期**（而不是函数调用期）就触发了 getStorage()，
 * 那时 search 可能已经被规整走了，只看 location.search 会退化成 indexeddb。
 * 曾经因为这一点，内存模式静默变成了持久化模式。
 */
export function preferredKind() {
  const fromSearch = new URLSearchParams(location.search).get('storage');
  if (fromSearch === 'memory') return 'memory';

  const hashQuery = (location.hash || '').split('?')[1] || '';
  const fromHash = new URLSearchParams(hashQuery).get('storage');
  if (fromHash === 'memory') return 'memory';

  return 'indexeddb';
}

/**
 * 单例存放位置的考量
 * ------------------------------------------------------------
 * 正常情况全站共享同一个模块实例，模块级变量就够了。
 * 但模块有可能被加载成两份（开发期 HMR、或某处用了动态 import()），
 * 这时模块级变量互不可见，会各自 new 一个适配器：
 *   - IndexedDB 无所谓（都指向同一个库）
 *   - MemoryAdapter 会变成两个互不相干的内存表 → "写进去读不出来"
 *
 * 所以把实例额外挂到 globalThis 上，跨模块副本共享。
 * 这样无论从哪个入口调用 getStorage()，拿到的都是同一个适配器。
 */
const GLOBAL_KEY = '__englishLearningStorage__';

function adopt(adapter) {
  globalThis[GLOBAL_KEY] = adapter;
  return adapter;
}

export function getStorage() {
  const shared = globalThis[GLOBAL_KEY];
  if (shared) return shared;
  return adopt(createStorage(preferredKind()));
}

/**
 * 强制替换单例（测试用）。
 * 传 null / undefined 表示重置，下次 getStorage() 会按 URL 参数重建。
 */
export function resetStorageForTests(adapter) {
  if (adapter) adopt(assertAdapter(adapter));
  else {
    try { globalThis[GLOBAL_KEY]?.close?.(); } catch { /* ignore */ }
    globalThis[GLOBAL_KEY] = null;
  }
}
